(ns practitest.frontend.forms3.entity-form.jira-parent-input
  (:require
   [clojure.string :as str]
   [goog.object :as g]
   [goog.string :as gstring]
   [goog.string.format]
   [practitest.frontend.common.i18n :refer [get-translation]]
   [practitest.frontend.components.searchable-list :refer [async-select custom-components
                                                             full-width-select-styles]]
   [practitest.frontend.routing.backend-routes :as broutes]
   [practitest.frontend.subs :as subs]
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]))

(def min-query-length 3)

(defn- parse-json-value
  "Current stored shape: a JSON string {\"id\":\"KEY\",\"name\":\"Summary\"}, written by the
   Jira sync (lib/integration_services/jira_common.rb#map_fields)."
  [raw-value]
  (when (seq raw-value)
    (try
      (let [parsed (js->clj (.parse js/JSON raw-value) :keywordize-keys true)]
        (when (:id parsed)
          {:value (:id parsed) :key (:id parsed) :summary (:name parsed)
           :label (str (:id parsed) " - " (:name parsed))}))
      (catch :default _ nil))))

(defn- parse-url-title-value
  "Alternate shape some read paths may already have converted the value to:
   {:url \"...\" :title \"KEY - Summary\"} (see lib/new_frontend_serializer.rb,
   serialize_custom_field_value's url? branch)."
  [{:keys [title]}]
  (when (seq title)
    (let [[key summary] (str/split title #" - " 2)]
      (when (seq key)
        {:value key :key key :summary (or summary "") :label title}))))

(defn- parse-stored-value
  "Backward-compatible: accepts either shape described above, whichever this read path hands us."
  [raw-value]
  (cond
    (map? raw-value) (parse-url-title-value raw-value)
    (string? raw-value) (parse-json-value raw-value)
    :else nil))

(defn- value->stored-string [value]
  (when value
    (gstring/format "{\"id\":%s,\"name\":%s}"
                    (.stringify js/JSON (:key value))
                    (.stringify js/JSON (:summary value)))))

(defn- transform-results [response]
  (clj->js
   (mapv (fn [issue]
           {:value (:key issue) :key (:key issue) :summary (:summary issue)
            :icon_url (:issue_type_icon_url issue) :label (str (:key issue) " - " (:summary issue))})
         (:results response))))

(defn- option-label [option]
  (let [icon-url (g/get option "icon_url")
        key (g/get option "key")
        summary (g/get option "summary")]
    (reagent/as-element
     [:div.jira-parent-option-label
      (when icon-url
        [:img {:src icon-url :width 16 :height 16 :alt ""}])
      [:span key]
      [:span.jira-parent-option-summary (str "- " summary)]])))

(defn- fetch-issues [url-template query jira-proj-id issue_type_id external-id callback fetch-state]
  (if (< (count query) min-query-length)
    (do (reset! fetch-state :too-short)
        (callback #js []))
    (let [request-url (gstring/format url-template (js/encodeURIComponent query) (or jira-proj-id "")
                                      (or issue_type_id "") (or external-id ""))]
      (-> (js/fetch request-url (clj->js {:credentials "include"}))
          (.then (fn [response]
                   (if (.-ok response)
                     (.json response)
                     (throw (js/Error. "jira-parent-search-failed")))))
          (.then (fn [data]
                   (let [clj-data (js->clj data :keywordize-keys true)]
                     (if (:errors clj-data)
                       (do (reset! fetch-state :error) (callback #js []))
                       (do (reset! fetch-state :ok) (callback (transform-results clj-data)))))))
          (.catch (fn [_]
                    (reset! fetch-state :error)
                    (callback #js [])))))))

(defn jira-parent-input [base-control {:keys [base-value]}]
  (let [project @(re-frame/subscribe [::subs/current-project])
        url-template (broutes/get-url "integration_setting_jira_two_ways#query_jira_issues"
                                      {:project_id (:id project)
                                       :integrated_bugtracker_id (:integrated_bugtracker_id project)})
        fetch-state (reagent/atom nil)
        selected (reagent/atom (parse-stored-value base-value))]
    (fn [base-control {:keys [issue_type_id external-id on-change raw-key-output? jira-proj-id errors]
                       :as   props}]
      @fetch-state
      [base-control (assoc props :custom-control? true)
       [async-select
        {:value (clj->js @selected)
         :hasError (boolean (seq errors))
         :cacheOptions false
         :defaultOptions false
         :isClearable true
         :menuPlacement "auto"
         :menuPosition "fixed"
         :placeholder (get-translation "entity.bugtracker.jira-parent.search-placeholder"
                                       "Search by key or name")
         :formatOptionLabel option-label
         :components custom-components
         :styles (full-width-select-styles)
         :noOptionsMessage (fn [_]
                             (case @fetch-state
                               :error (get-translation "entity.bugtracker.jira-parent.search-error"
                                                       "Couldn't load results from Jira. Try again.")
                               :ok (get-translation "entity.bugtracker.jira-parent.no-matches"
                                                    "No matching issues")
                               (get-translation "entity.bugtracker.jira-parent.type-to-search"
                                                "Type at least 3 characters to search")))
         :loadOptions (fn [input-value callback]
                        (fetch-issues url-template input-value jira-proj-id issue_type_id external-id
                                     callback fetch-state))
         :onChange (fn [js-value]
                     (let [value (when js-value (js->clj js-value :keywordize-keys true))]
                       (reset! selected value)
                       (on-change (if raw-key-output?
                                    (:key value)
                                    (value->stored-string value)))))}]])))
